import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { PhoneInput, isValidPhone } from "@/components/PhoneInput";
import { PreferencesCollapsible } from "@/components/PreferencesCollapsible";
import { EnableMaestroCard } from "@/components/EnableMaestroCard";
import { toast } from "sonner";
import { Loader2, UserRound, Save, Lightbulb } from "lucide-react";

export default function AlumnoProfile() {
  const { user, checkAuth } = useAuth();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ name: "", phone: "", preferences: [] });
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (user) {
      setF({ name: user.name || "", phone: user.phone || "", preferences: user.preferences || [] });
      setReady(true);
    }
  }, [user]);

  const togglePref = (c) => setF((p) => ({
    ...p,
    preferences: p.preferences.includes(c) ? p.preferences.filter((x) => x !== c) : [...p.preferences, c],
  }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Ingresa tu nombre"); return; }
    if (f.phone && !isValidPhone(f.phone)) { toast.error("Ingresa un celular válido o déjalo vacío"); return; }
    setLoading(true);
    try {
      await api.put("/auth/profile", { name: f.name.trim(), phone: f.phone || null, preferences: f.preferences });
      await checkAuth();
      toast.success("Se han guardado los datos");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!ready) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary"><UserRound className="w-6 h-6" /></span>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Mi Perfil</h1>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>
      </div>

      <Card className="p-6 space-y-5" data-testid="alumno-profile">
        <div>
          <Label>Nombre</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Tu nombre" data-testid="profile-name" />
          <p className="text-xs text-muted-foreground mt-1.5">Aparecerá en tus comprobantes, certificados y mensajes de la plataforma.</p>
        </div>
        <div>
          <Label>Teléfono móvil <span className="text-muted-foreground font-normal">(opcional, recomendado)</span></Label>
          <PhoneInput value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="profile-phone" />
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200" data-testid="profile-phone-tip">
            <Lightbulb className="w-4 h-4 shrink-0 mt-px" />
            <span>Te recomendamos ingresar tu número de teléfono móvil. Te facilitará la comunicación directa por WhatsApp con los Maestros de talleres.</span>
          </div>
        </div>
        <PreferencesCollapsible title="Mis intereses" categories={cats} selected={f.preferences} onToggle={togglePref}
          defaultOpen={(user?.preferences || []).length > 0} testId="profile-preferences"
          hint="Al guardar, tus recomendaciones y el filtrado de Explorar se actualizan con estos intereses." />
        <Button onClick={save} disabled={loading} data-testid="save-profile">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Guardar Cambios</>}
        </Button>
      </Card>
      <EnableMaestroCard />
    </div>
  );
}
